import { type REGION, countryEnumBase, getTicketsUrl, isDefined, isEmpty, regionEnumBase, registeredTicketsRoutes } from '@nfctron/nexus-core';

import { type Organization as JsonLdOrganization, type Place as JsonLdPlace, type Thing as JsonLdThing, type WithContext } from 'schema-dts';

/**
 * Generic JsonLd component props
 */
export interface JsonLdProps<TThing extends JsonLdThing> {
	/** The key to use for the script tag */
	id: string;
	/** The structured data to include in the script tag */
	data: TThing;
}

/**
 * Generic JsonLd component that renders JSON-LD structured data in a script tag
 * @see https://schema.org/Event
 * @see https://schema.org/Offer
 *
 * @author filipditrich <ditrich@nfctron.com>
 */
/**
 * Generic JsonLd component that renders JSON-LD structured data in a script tag
 * @see https://schema.org/Event
 * @see https://schema.org/Offer
 */
export function JsonLd<TThing extends JsonLdThing>(props: JsonLdProps<TThing>) {
	const { id, data } = props;

	return (
		<script
			id={id}
			type="application/ld+json"
			/* eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml */
			dangerouslySetInnerHTML={{
				__html: JSON.stringify({ '@context': 'https://schema.org', ...(typeof data === 'object' ? data : { '@type': 'Text', text: data }) }),
			}}
		/>
	);
}
/**
 * Creates a JSON-LD representation of an organizer
 */
export function createJsonLdOrganizer(
	params: { namePub: string; slug: string; logoUrl?: string },
	extendWith?: Partial<Exclude<JsonLdOrganization, string>>,
) {
	return {
		'@type': 'Organization',
		name: params.namePub,
		url: getTicketsUrl(registeredTicketsRoutes.organizerDetail({ organizerSlug: params.slug })).href,
		logo: !isEmpty(params.logoUrl) ? params.logoUrl : undefined,
		...extendWith,
	} as const satisfies JsonLdOrganization;
}

/**
 * Creates a JSON-LD representation of a place
 */
export function createJsonLdPlace(
	params: {
		place: string;
		regionId: REGION;
		locationVenue?: {
			namePub: string;
			coordinates: [number, number];
		};
	},
	extendWith?: Partial<Exclude<JsonLdPlace, string>>,
) {
	if (isDefined(params.locationVenue)) {
		return {
			'@type': 'Place',
			name: params.locationVenue.namePub,
			geo: {
				'@type': 'GeoCoordinates',
				latitude: params.locationVenue.coordinates[0],
				longitude: params.locationVenue.coordinates[1],
			},
			address: {
				'@type': 'PostalAddress',
				streetAddress: params.place,
				addressCountry: countryEnumBase.get(regionEnumBase.get(params.regionId).countryId).isoChar2,
			},
			...extendWith,
		} as const satisfies JsonLdPlace;
	}

	return {
		'@type': 'Place',
		name: params.place,
		address: {
			'@type': 'PostalAddress',
			streetAddress: params.place,
			addressCountry: countryEnumBase.get(regionEnumBase.get(params.regionId).countryId).isoChar2,
		},
		...extendWith,
	} as const satisfies JsonLdPlace;
}
